import io from "socket.io-client";
import { getToken, getUser } from "./auth";

const resolveSocketUrl = () => {
  const explicit = process.env.REACT_APP_SOCKET_URL?.trim();
  if (explicit) {
    return explicit;
  }

  const apiUrl = process.env.REACT_APP_URL?.trim();
  if (apiUrl) {
    return apiUrl.replace(/\/api\/?$/i, "");
  }

  return "http://localhost:1980";
};

const SOCKET_URL = resolveSocketUrl();

export const SOCKET_EVENTS = {
  APPOINTMENT_BOOKED: "appointment-booked",
  APPOINTMENT_UPCOMING: "appointment-upcoming",
};

class SocketManager {
  constructor() {
    this.socket = null;
    this.isAuthenticated = false;
    this.authStateListeners = new Set();
    this.appointmentBookedCallbacks = new Set();
    this.appointmentUpcomingCallbacks = new Set();
  }

  connect() {
    const token = getToken();
    if (!token) {
      if (process.env.NODE_ENV === "development") {
        console.warn("No token available, socket connection skipped");
      }
      return;
    }

    if (this.socket?.connected) {
      this.authenticate();
      return;
    }

    if (this.socket && !this.socket.connected) {
      this.socket.connect();
      return;
    }

    this.socket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      timeout: 20000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      withCredentials: true,
      auth: { token },
    });

    this.setupConnectionHandlers();
    this.attachDomainEventBridges();
  }

  reconnect() {
    const token = getToken();
    if (!token) {
      this.fullDisconnect();
      return;
    }

    // Fresh socket ensures updated auth token and event bridges after login/branch switch.
    this.fullDisconnect();
    this.connect();
  }

  attachDomainEventBridges() {
    if (!this.socket) {
      return;
    }

    this.socket.off(SOCKET_EVENTS.APPOINTMENT_BOOKED);
    this.socket.off(SOCKET_EVENTS.APPOINTMENT_UPCOMING);

    this.socket.on(SOCKET_EVENTS.APPOINTMENT_BOOKED, (data) => {
      this.appointmentBookedCallbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          if (process.env.NODE_ENV === "development") {
            console.error("appointment-booked handler error:", error);
          }
        }
      });
    });

    this.socket.on(SOCKET_EVENTS.APPOINTMENT_UPCOMING, (data) => {
      this.appointmentUpcomingCallbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          if (process.env.NODE_ENV === "development") {
            console.error("appointment-upcoming handler error:", error);
          }
        }
      });
    });
  }

  setupConnectionHandlers() {
    const handleConnect = () => {
      if (process.env.NODE_ENV === "development") {
        console.log("Socket connected:", this.socket.id);
      }
      this.authenticate();
      this.notifyAuthStateListeners();
    };

    const handleDisconnect = () => {
      if (process.env.NODE_ENV === "development") {
        console.log("Socket disconnected");
      }
      this.isAuthenticated = false;
      this.notifyAuthStateListeners();
    };

    const handleConnectError = (error) => {
      if (process.env.NODE_ENV === "development") {
        console.error("Socket connection error:", error);
      }
      this.notifyAuthStateListeners();
    };

    const handleError = (error) => {
      if (process.env.NODE_ENV === "development") {
        console.error("Socket error:", error);
      }
    };

    this.socket.on("connect", handleConnect);
    this.socket.on("disconnect", handleDisconnect);
    this.socket.on("connect_error", handleConnectError);
    this.socket.on("error", handleError);
  }

  authenticate() {
    if (!this.socket?.connected) {
      return;
    }

    const user = getUser();
    const token = getToken();

    if (!user || !token) {
      if (process.env.NODE_ENV === "development") {
        console.warn("User or token not available for socket authentication");
      }
      return;
    }

    this.socket.emit(
      "authenticate",
      {
        userId: user.id,
        facilityId: user.facility?.id || user.facilityId,
        facilityBranchId: user.branchId,
        token,
      },
      (response) => {
        if (response?.success) {
          this.isAuthenticated = true;
          if (process.env.NODE_ENV === "development") {
            console.log("Socket authenticated");
          }
        } else {
          this.isAuthenticated = false;
          if (process.env.NODE_ENV === "development") {
            console.error("Socket authentication failed:", response?.error);
          }
        }
        this.notifyAuthStateListeners();
      }
    );
  }

  onAuthStateChange(callback) {
    this.authStateListeners.add(callback);
    return () => this.authStateListeners.delete(callback);
  }

  notifyAuthStateListeners() {
    this.authStateListeners.forEach((callback) => {
      try {
        callback();
      } catch {
        // ignore listener errors
      }
    });
  }

  isSocketConnected() {
    return this.socket?.connected || false;
  }

  isSocketAuthenticated() {
    return this.isAuthenticated && this.isSocketConnected();
  }

  getSocketId() {
    return this.socket?.id;
  }

  on(event, callback) {
    if (!this.socket) {
      return () => {};
    }

    this.socket.on(event, callback);
    return () => this.socket?.off(event, callback);
  }

  off(event, callback) {
    if (this.socket) {
      if (callback) {
        this.socket.off(event, callback);
      } else {
        this.socket.off(event);
      }
    }
  }

  emit(event, data) {
    if (!this.isSocketConnected()) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Socket not connected, cannot emit:", event);
      }
      return;
    }

    this.socket.emit(event, data);
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
    }
    this.isAuthenticated = false;
    this.notifyAuthStateListeners();
  }

  fullDisconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.isAuthenticated = false;
    this.notifyAuthStateListeners();
  }

  onAppointmentBooked(callback) {
    this.appointmentBookedCallbacks.add(callback);
    return () => this.appointmentBookedCallbacks.delete(callback);
  }

  onAppointmentUpcoming(callback) {
    this.appointmentUpcomingCallbacks.add(callback);
    return () => this.appointmentUpcomingCallbacks.delete(callback);
  }
}

export const socketManager = new SocketManager();
